// src/pages/ExpensePurchaseOrder/MissingEpoDashboard.js
//
// Batch 3 / Rounds 8–10 — No-EPO / Missing-EPO Shipment dashboard.
//
// Shipping records packages that arrived WITHOUT an EPO (e.g. an employee
// ordered from Amazon directly), notifies the employee + their manager
// (email + in-app), and later links the EPO the employee creates. Resolved /
// cancelled records move to an Archive view (never hard-deleted).
//
// Backend: /api/epo/missing-epo (+ /{id}/notify, /{id}/link-epo, /{id}/status).
// Access: route gated by `epo.missingEpo`; the API re-checks CanManageMissingEpo
// (the "EPO - Missing-EPO Shipments" permission OR IT-Admin), assignable to any
// role/user in Access Management.

import React, { useState, useEffect, useCallback } from "react";
import {
    Container, Row, Col, Card, CardBody, Table, Button, Badge, Spinner, Alert, Input, Label,
    Modal, ModalHeader, ModalBody, ModalFooter
} from "reactstrap";
import { DashboardPageHeader } from "../../Components/Common/DashboardKit";
import api from "../../api";
import { epoErrorMessage } from "./epoErrors";  // Batch 5 / Round 13

const pick = (o, ...keys) => { for (const k of keys) if (o && o[k] !== undefined && o[k] !== null) return o[k]; return undefined; };
const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString(); } catch { return "—"; } };
const fmtDateTime = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleString(); } catch { return "—"; } };
const fmtMoney = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

const STATUS_META = {
    OPEN: { label: "Open / Needs EPO", color: "danger" },
    EMPLOYEE_NOTIFIED: { label: "Employee Notified", color: "warning" },
    MANAGER_NOTIFIED: { label: "Manager Notified", color: "warning" },
    EPO_CREATED: { label: "EPO Created", color: "info" },
    CLOSED: { label: "Closed / Resolved", color: "success" },
    CANCELLED: { label: "Cancelled / Invalid", color: "secondary" },
};
const statusBadge = (s) => {
    const m = STATUS_META[(s || "").toUpperCase()] || { label: s || "—", color: "light" };
    return <Badge color={m.color}>{m.label}</Badge>;
};

const blankForm = {
    requesterEmpId: "", company: "", description: "",
    qty: "", amount: "", trackingNumber: "", packageReceivedDate: "", comments: "",
};

const MissingEpoDashboard = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [search, setSearch] = useState("");
    const [busyId, setBusyId] = useState(null);

    // employee directory for the requester picker
    const [employees, setEmployees] = useState([]);

    // create/edit modal
    const [formOpen, setFormOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(blankForm);
    const [saving, setSaving] = useState(false);

    // link-EPO modal
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkId, setLinkId] = useState(null);
    const [linkEpoNum, setLinkEpoNum] = useState("");

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await api.get("/epo/missing-epo", {
                params: { includeArchived: showArchived, search: search || undefined, page: 1, pageSize: 200 },
            });
            const data = res.data || {};
            setRows(data.Items ?? data.items ?? []);
            setTotal(data.Total ?? data.total ?? 0);
        } catch (e) {
            setError(epoErrorMessage(e, "Failed to load.", {
                403: "You don't have permission to view the Missing-EPO tracker.",
            }));
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [showArchived, search]);

    useEffect(() => { load(); }, [load]);

    // load the SSW (Florida) employee directory once (for the requester
    // dropdown). Uses the dedicated endpoint that filters empbasic by
    // Company == 'SSW' and active status — NOT OrgChart (which returned the
    // wrong/India set). Each entry carries its resolved supervisor so we can
    // show the auto-selected manager.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get("/epo/missing-epo/employees");
                const data = Array.isArray(res.data) ? res.data : [];
                const list = data.map((e) => ({
                    empId: pick(e, "empId", "EmpId"),
                    name: pick(e, "name", "Name") || pick(e, "empId", "EmpId"),
                    supervisorId: pick(e, "supervisorId", "SupervisorId"),
                    supervisorName: pick(e, "supervisorName", "SupervisorName"),
                })).filter((e) => e.empId);
                list.sort((a, b) => a.name.localeCompare(b.name));
                if (!cancelled) setEmployees(list);
            } catch { /* directory is a convenience; manual entry still works */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const openCreate = () => { setEditId(null); setForm(blankForm); setFormOpen(true); };
    const openEdit = (r) => {
        setEditId(r.Id ?? r.id);
        setForm({
            requesterEmpId: pick(r, "RequesterEmpId", "requesterEmpId") || "",
            company: pick(r, "Company", "company") || "",
            description: pick(r, "Description", "description") || "",
            qty: pick(r, "Qty", "qty") ?? "",
            amount: pick(r, "Amount", "amount") ?? "",
            trackingNumber: pick(r, "TrackingNumber", "trackingNumber") || "",
            packageReceivedDate: (pick(r, "PackageReceivedDate", "packageReceivedDate") || "").slice(0, 10),
            comments: pick(r, "Comments", "comments") || "",
        });
        setFormOpen(true);
    };

    // Picking a requester is all that's needed — the manager is resolved
    // automatically from empbasic.SupervisorID on the server. We only track the
    // requester; the resolved manager NAME is shown read-only for confirmation.
    const onPickRequester = (empId) => setF("requesterEmpId", empId);
    const selectedEmployee = employees.find((e) => e.empId === form.requesterEmpId);

    const saveForm = async () => {
        setSaving(true); setError(null);
        try {
            const body = {
                requesterEmpId: form.requesterEmpId || null,
                // managerEmpId intentionally omitted — the server resolves the
                // manager from empbasic.SupervisorID automatically.
                company: form.company || null,
                description: form.description || null,
                qty: form.qty === "" ? null : Number(form.qty),
                amount: form.amount === "" ? null : Number(form.amount),
                trackingNumber: form.trackingNumber || null,
                packageReceivedDate: form.packageReceivedDate || null,
                comments: form.comments || null,
            };
            if (editId) await api.put(`/epo/missing-epo/${editId}`, body);
            else await api.post("/epo/missing-epo", body);
            setFormOpen(false);
            setNotice(editId ? "Record updated." : "Missing-EPO record created.");
            await load();
        } catch (e) {
            setError(epoErrorMessage(e, "Save failed."));
        } finally {
            setSaving(false);
        }
    };

    const wrap = async (id, fn, msg) => {
        setBusyId(id); setError(null); setNotice(null);
        try { await fn(); if (msg) setNotice(msg); await load(); }
        catch (e) { setError(epoErrorMessage(e, "Action failed.")); }
        finally { setBusyId(null); }
    };

    const notify = (r) => {
        const id = r.Id ?? r.id;
        wrap(id, async () => {
            const res = await api.post(`/epo/missing-epo/${id}/notify`, { notifyEmployee: true, notifyManager: true });
            const w = res.data?.Warning ?? res.data?.warning;
            if (w) setNotice(`Notifications sent. Note: ${w}`);
            else setNotice("Employee and manager notified (email + in-app).");
        });
    };

    const openLink = (r) => { setLinkId(r.Id ?? r.id); setLinkEpoNum(pick(r, "LinkedEpoNum", "linkedEpoNum") || ""); setLinkOpen(true); };
    const saveLink = async () => {
        const n = Number(linkEpoNum);
        if (!Number.isInteger(n) || n <= 0) { setError("Enter a valid EPO number to link."); return; }
        await wrap(linkId, async () => {
            await api.post(`/epo/missing-epo/${linkId}/link-epo`, { linkedEpoNum: n, markEpoCreated: true });
        }, `Linked to EPO #${n} and marked EPO Created.`);
        setLinkOpen(false);
    };

    const setStatus = (r, status, msg) => {
        const id = r.Id ?? r.id;
        wrap(id, async () => { await api.post(`/epo/missing-epo/${id}/status`, { status }); }, msg);
    };

    const exportCsv = () => {
        const cols = [
            ["Id", (r) => pick(r, "Id", "id")],
            ["Requester", (r) => pick(r, "RequesterName", "requesterName") || pick(r, "RequesterEmpId", "requesterEmpId")],
            ["Manager", (r) => pick(r, "ManagerName", "managerName") || pick(r, "ManagerEmpId", "managerEmpId")],
            ["Vendor", (r) => pick(r, "Company", "company")],
            ["Description", (r) => pick(r, "Description", "description")],
            ["Qty", (r) => pick(r, "Qty", "qty")],
            ["Amount", (r) => pick(r, "Amount", "amount")],
            ["Tracking", (r) => pick(r, "TrackingNumber", "trackingNumber")],
            ["Received", (r) => fmtDate(pick(r, "PackageReceivedDate", "packageReceivedDate"))],
            ["Status", (r) => (STATUS_META[(pick(r, "Status", "status") || "").toUpperCase()]?.label) || pick(r, "Status", "status")],
            ["Linked EPO", (r) => pick(r, "LinkedEpoNum", "linkedEpoNum")],
            ["Created By", (r) => pick(r, "CreatedByName", "createdByName") || pick(r, "CreatedBy", "createdBy")],
            ["Created On", (r) => fmtDateTime(pick(r, "CreatedOn", "createdOn"))],
        ];
        const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
        const lines = [cols.map((c) => c[0]).join(",")];
        for (const r of rows) lines.push(cols.map((c) => esc(c[1](r))).join(","));
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `missing-epo-${showArchived ? "archive" : "active"}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-mail-send-line"
                    eyebrow="EPO"
                    title="Missing-EPO Shipments"
                    subtitle="Track packages received without an EPO and notify the employee and manager."
                    tone="violet"
                    breadcrumb={[
                        { label: "Dashboard", to: "/" },
                        { label: "EPO", to: "/workflows?wf=epo" },
                        { label: "Missing-EPO Shipments" },
                    ]}
                />

                {error && <Alert color="danger" className="mt-3" toggle={() => setError(null)}>{error}</Alert>}
                {notice && <Alert color="success" className="mt-3" toggle={() => setNotice(null)}>{notice}</Alert>}

                <Card className="mt-3">
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                <Button color="primary" size="sm" onClick={openCreate}>
                                    <i className="ri-add-line align-bottom me-1" />New Missing-EPO Record
                                </Button>
                                <div style={{ minWidth: 220 }}>
                                    <Input bsSize="sm" placeholder="Search vendor / tracking / EPO…"
                                        value={search} onChange={(e) => setSearch(e.target.value)} />
                                </div>
                                <div className="form-check form-switch">
                                    <Input type="switch" id="archToggle" checked={showArchived}
                                        onChange={(e) => setShowArchived(e.target.checked)} />
                                    <Label for="archToggle" className="form-check-label small">Show archived</Label>
                                </div>
                            </div>
                            <Button color="light" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
                                <i className="ri-download-2-line align-bottom me-1" />Export CSV ({total})
                            </Button>
                        </div>

                        {loading ? (
                            <div><Spinner size="sm" className="me-2" />Loading…</div>
                        ) : rows.length === 0 ? (
                            <div className="text-muted">
                                {showArchived ? "No archived records." : "No active Missing-EPO records. Click “New Missing-EPO Record” to add one."}
                            </div>
                        ) : (
                            <div className="table-responsive epo-sticky-head">
                                <Table size="sm" hover className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Requester</th><th>Manager</th><th>Vendor</th><th>Description</th>
                                            <th className="text-end">Qty</th><th className="text-end">Amount</th>
                                            <th>Tracking</th><th>Received</th><th>Status</th><th>Linked EPO</th><th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => {
                                            const id = pick(r, "Id", "id");
                                            const status = (pick(r, "Status", "status") || "").toUpperCase();
                                            const busy = busyId === id;
                                            const linked = pick(r, "LinkedEpoNum", "linkedEpoNum");
                                            const archived = status === "CLOSED" || status === "CANCELLED";
                                            return (
                                                <tr key={id}>
                                                    <td>{pick(r, "RequesterName", "requesterName") || pick(r, "RequesterEmpId", "requesterEmpId") || "—"}</td>
                                                    <td>{pick(r, "ManagerName", "managerName") || pick(r, "ManagerEmpId", "managerEmpId") || "—"}</td>
                                                    <td>{pick(r, "Company", "company") || "—"}</td>
                                                    <td style={{ maxWidth: 240 }} className="text-truncate" title={pick(r, "Description", "description") || ""}>
                                                        {pick(r, "Description", "description") || "—"}</td>
                                                    <td className="text-end">{pick(r, "Qty", "qty") ?? "—"}</td>
                                                    <td className="text-end">{fmtMoney(pick(r, "Amount", "amount"))}</td>
                                                    <td>{pick(r, "TrackingNumber", "trackingNumber") || "—"}</td>
                                                    <td>{fmtDate(pick(r, "PackageReceivedDate", "packageReceivedDate"))}</td>
                                                    <td>{statusBadge(status)}</td>
                                                    <td>{linked ? <Badge color="info" pill>#{linked}</Badge> : "—"}</td>
                                                    <td className="text-nowrap">
                                                        {busy ? <Spinner size="sm" /> : (
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {!archived && (
                                                                    <Button size="sm" color="link" className="p-0" onClick={() => openEdit(r)}>Edit</Button>
                                                                )}
                                                                {!archived && (
                                                                    <Button size="sm" color="link" className="p-0" onClick={() => notify(r)}>Notify</Button>
                                                                )}
                                                                {!archived && (
                                                                    <Button size="sm" color="link" className="p-0" onClick={() => openLink(r)}>Link EPO</Button>
                                                                )}
                                                                {!archived && (
                                                                    <Button size="sm" color="link" className="p-0 text-success" onClick={() => setStatus(r, "CLOSED", "Record closed and archived.")}>Close</Button>
                                                                )}
                                                                {!archived && (
                                                                    <Button size="sm" color="link" className="p-0 text-muted" onClick={() => setStatus(r, "CANCELLED", "Record cancelled and archived.")}>Cancel</Button>
                                                                )}
                                                                {archived && (
                                                                    <Button size="sm" color="link" className="p-0" onClick={() => setStatus(r, "OPEN", "Record reopened.")}>Reopen</Button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>

            {/* Create / Edit modal */}
            <Modal isOpen={formOpen} toggle={() => setFormOpen(false)} size="lg">
                <ModalHeader toggle={() => setFormOpen(false)}>
                    {editId ? "Edit Missing-EPO Record" : "New Missing-EPO Record"}
                </ModalHeader>
                <ModalBody>
                    <Row className="g-3">
                        <Col md={6}>
                            <Label className="form-label small mb-1">Employee / Requester</Label>
                            <Input type="select" value={form.requesterEmpId} onChange={(e) => onPickRequester(e.target.value)}>
                                <option value="">— select employee —</option>
                                {employees.map((e) => <option key={e.empId} value={e.empId}>{e.name} ({e.empId})</option>)}
                            </Input>
                        </Col>
                        <Col md={6}>
                            <Label className="form-label small mb-1">Manager / Supervisor</Label>
                            <Input
                                plaintext
                                readOnly
                                value={
                                    selectedEmployee
                                        ? (selectedEmployee.supervisorName
                                            ? `${selectedEmployee.supervisorName} (${selectedEmployee.supervisorId})`
                                            : (selectedEmployee.supervisorId
                                                ? `Supervisor ${selectedEmployee.supervisorId}`
                                                : "No supervisor on file"))
                                        : "—"
                                }
                                className="ps-2 mb-0"
                            />
                            <div className="form-text">Resolved automatically from the employee's supervisor (empbasic).</div>
                        </Col>
                        <Col md={6}>
                            <Label className="form-label small mb-1">Vendor / company</Label>
                            <Input value={form.company} onChange={(e) => setF("company", e.target.value)} placeholder="e.g. Amazon" />
                        </Col>
                        <Col md={3}>
                            <Label className="form-label small mb-1">Quantity</Label>
                            <Input type="number" value={form.qty} onChange={(e) => setF("qty", e.target.value)} />
                        </Col>
                        <Col md={3}>
                            <Label className="form-label small mb-1">Amount</Label>
                            <Input type="number" value={form.amount} onChange={(e) => setF("amount", e.target.value)} />
                        </Col>
                        <Col md={12}>
                            <Label className="form-label small mb-1">Items / description</Label>
                            <Input type="textarea" rows={2} value={form.description} onChange={(e) => setF("description", e.target.value)} />
                        </Col>
                        <Col md={6}>
                            <Label className="form-label small mb-1">Tracking number</Label>
                            <Input value={form.trackingNumber} onChange={(e) => setF("trackingNumber", e.target.value)} />
                        </Col>
                        <Col md={6}>
                            <Label className="form-label small mb-1">Package received date</Label>
                            <Input type="date" value={form.packageReceivedDate} onChange={(e) => setF("packageReceivedDate", e.target.value)} />
                        </Col>
                        <Col md={12}>
                            <Label className="form-label small mb-1">Shipping comments</Label>
                            <Input type="textarea" rows={2} value={form.comments} onChange={(e) => setF("comments", e.target.value)} />
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
                    <Button color="primary" onClick={saveForm} disabled={saving}>
                        {saving ? <><Spinner size="sm" className="me-1" />Saving…</> : (editId ? "Save Changes" : "Create Record")}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Link-EPO modal */}
            <Modal isOpen={linkOpen} toggle={() => setLinkOpen(false)}>
                <ModalHeader toggle={() => setLinkOpen(false)}>Link to EPO</ModalHeader>
                <ModalBody>
                    <Label className="form-label small mb-1">EPO number the employee created</Label>
                    <Input type="number" value={linkEpoNum} onChange={(e) => setLinkEpoNum(e.target.value)} placeholder="e.g. 85781" />
                    <div className="form-text">This marks the record as “EPO Created” and links the EPO number.</div>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setLinkOpen(false)}>Cancel</Button>
                    <Button color="primary" onClick={saveLink}>Link EPO</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default MissingEpoDashboard;
